import React, { useEffect, useState, useCallback } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spinner,
} from 'react-bootstrap';
import { dataService } from '../../services/DataService';
import { useAppDispatch, useAppSelector } from '../reducer/reduxHooks';
import { fetchModules } from '../reducer/thunks';
import hashFileName from '../../utils/hashFileName';

interface ModuleBaseEditorProps {
  moduleId: string;
}

// Using loose Record typing consistent with existing module slice usage
export default function ModuleBaseEditor({ moduleId }: ModuleBaseEditorProps) {
  const dispatch = useAppDispatch();
  const modules = useAppSelector((state) => state.moduleState.modules);
  const existing = modules.find((m: Record<string, any>) => m.id === moduleId) || {};
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError(null);
      setSaved(false);
      try {
        // Prefer store value, fallback to direct fetch
        if (Object.keys(existing).length > 0) {
          setFormValues(existing);
        } else {
          const res = await dataService.getItem('modules', moduleId);
          if (active) {
            if (res.statusCode === 200) {
              setFormValues(res.body);
            } else {
              setError('Modul konnte nicht geladen werden');
            }
          }
        }
      } catch (e) {
        setError('Fehler beim Laden');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [moduleId, existing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: name === 'order' ? Number(value) : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      let imgSrcValue = formValues.imgSrc || '';

      // If a new image file is selected, upload it first (hash based naming)
      if (selectedImageFile) {
        setUploadingImage(true);
        try {
          const hashedName = await hashFileName(selectedImageFile);
          const uploadPath = `modules/${moduleId}/image/${hashedName}`;
          const uploadRes = await dataService.uploadFile(selectedImageFile, uploadPath);
          imgSrcValue = uploadRes.url;
        } catch (imgErr: any) {
          console.error('Image upload failed', imgErr);
          setError('Bild Upload fehlgeschlagen');
          setUploadingImage(false);
          setSaving(false);
          return;
        } finally {
          setUploadingImage(false);
        }
      }
      const payload = {
        title: formValues.title || '',
        title_fr: formValues.title_fr || '',
        title_it: formValues.title_it || '',
        description: formValues.description || '',
        description_fr: formValues.description_fr || '',
        description_it: formValues.description_it || '',
        domain: formValues.domain || '',
        // imgSrc now may be a remote storage URL; legacy relative paths still supported.
        imgSrc: imgSrcValue,
        order: formValues.order || 0,
        status: formValues.status || 'draft',
        id: moduleId,
      };
      const res = await dataService.updateItem('modules', moduleId, payload);
      if ([200, 201].includes(res.statusCode)) {
        setSaved(true);
        // Refresh modules list
        fetchModules(dispatch);
      } else {
        setError('Speichern fehlgeschlagen');
      }
    } catch (e) {
      setError('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" className="me-2" />
        <span>Modul wird geladen...</span>
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit} className="mt-3">
      {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
      {saved && <div className="alert alert-success py-2" role="alert">Gespeichert</div>}
      <Row className="g-3">
        <Col md={4}>
          <Form.Group controlId="moduleTitle">
            <Form.Label>Titel (DE)</Form.Label>
            <Form.Control name="title" value={formValues.title || ''} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="moduleTitleFr">
            <Form.Label>Titel (FR)</Form.Label>
            <Form.Control name="title_fr" value={formValues.title_fr || ''} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="moduleTitleIt">
            <Form.Label>Titel (IT)</Form.Label>
            <Form.Control name="title_it" value={formValues.title_it || ''} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="moduleDomain" className="mt-2">
            <Form.Label>Domain</Form.Label>
            <Form.Control name="domain" value={formValues.domain || ''} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="moduleOrder" className="mt-2">
            <Form.Label>Reihenfolge</Form.Label>
            <Form.Control type="number" name="order" value={formValues.order ?? 0} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="moduleStatus" className="mt-2">
            <Form.Label>Status</Form.Label>
            <Form.Select name="status" value={formValues.status || 'draft'} onChange={handleChange}>
              <option value="draft">Entwurf</option>
              <option value="published">Veröffentlicht</option>
              <option value="archived">Archiviert</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={12} className="mt-3">
          <Form.Group controlId="moduleImgSrc">
            <Form.Label>Modul Bild (imgSrc)</Form.Label>
            <Form.Control
              className="mb-2"
              name="imgSrc"
              placeholder="Bestehender Pfad oder Remote URL"
              value={formValues.imgSrc || ''}
              onChange={handleChange}
            />
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                const { files } = ev.target;
                const file = files && files[0] ? files[0] : null;
                setSelectedImageFile(file);
                if (file) {
                  const objectUrl = URL.createObjectURL(file);
                  setImagePreview(objectUrl);
                } else {
                  setImagePreview(null);
                }
              }}
            />
            {imagePreview && (
              <div className="mt-2">
                <strong>Vorschau:</strong>
                <div><img src={imagePreview} alt="Bild Vorschau" style={{ maxWidth: '200px', height: 'auto' }} /></div>
              </div>
            )}
            {uploadingImage && (
              <div className="mt-2 small text-muted">Bild wird hochgeladen...</div>
            )}
            {/* Hinweis zu späterer Bereinigung alter Bilder */}
            <div className="form-text">Falls ein neues Bild gewählt wird, wird das alte aktuell nicht gelöscht (Cleanup folgt).</div>
          </Form.Group>
        </Col>
        <Col md={12} className="mt-3">
          <Form.Group controlId="moduleDescription">
            <Form.Label>Beschreibung (DE)</Form.Label>
            <Form.Control as="textarea" rows={4} name="description" value={formValues.description || ''} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={12} className="mt-3">
          <Form.Group controlId="moduleDescriptionFr">
            <Form.Label>Beschreibung (FR)</Form.Label>
            <Form.Control as="textarea" rows={4} name="description_fr" value={formValues.description_fr || ''} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={12} className="mt-3">
          <Form.Group controlId="moduleDescriptionIt">
            <Form.Label>Beschreibung (IT)</Form.Label>
            <Form.Control as="textarea" rows={4} name="description_it" value={formValues.description_it || ''} onChange={handleChange} />
          </Form.Group>
        </Col>
      </Row>
      <div className="mt-4">
        <Button type="submit" disabled={saving}>{saving ? 'Speichern...' : 'Speichern'}</Button>
      </div>
    </Form>
  );
}
